import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectShowSecondLevelTabs } from 'selectors/partnerFormSelectors';
import EmbeddedIFrame from 'views/Frames/EmbeddedIFrame';
import { BrTabItems } from 'views/BrTabs/BrTabs';
import { GlobalState } from 'reducers/stateTypes';
import { useRuntimeConfig } from 'config/useRuntimeConfig';
import { NavigationWizard } from 'views/NavigationWizard/components/NavigationWizard';
import { NavigationWizardModeStateMachineOutput } from 'views/BrTabs/types/navigationWizardMode';
import { HEADER_HEIGHT, TOP_MENU_HEIGHT, TWO_LEVEL_TABS_HEIGHT, ONE_LEVEL_TABS_HEIGHT } from 'utils/constants';

interface BrTabFramesProps {
  pcoUrl: string;
  pcdV2DistributionUrl: string;
  pcdV2DocOverviewUrl: string;
  navigationWizardMode: NavigationWizardModeStateMachineOutput;
  urlHash?: string;
  onNavigate: (tabId: BrTabItems) => void;
}

const BrTabsFrames = ({
  pcoUrl,
  pcdV2DistributionUrl,
  pcdV2DocOverviewUrl,
  navigationWizardMode,
  urlHash,
  onNavigate,
}: BrTabFramesProps) => {
  const selectedTab = useSelector((state: GlobalState) => state.brTabs.tab);
  const brIdFromStore = useSelector((state: GlobalState) => state.caseInformation.accountInformation?.brUid) || '';
  const userId = useSelector((state: GlobalState) => state?.user?.userDetails?.username);
  const [iframeKey, setIframeKey] = useState(0);
  const showSecondLevelTabs = useSelector(selectShowSecondLevelTabs);
  const runtimeConfig = useRuntimeConfig();

  useEffect(() => {
    setIframeKey((prevState) => prevState + 1);
  }, [selectedTab]);

  const tabDetails = {
    [BrTabItems.CORE_DATA]: {
      url: `${runtimeConfig.configData.iframeUrls.brCoreIframe}/business-relationship/${brIdFromStore}/core-data`,
      title: 'BR core data',
    },
    [BrTabItems.BR_RELATIONS]: {
      url: `${runtimeConfig.configData.iframeUrls.brCoreIframe}/business-relationship/${brIdFromStore}/br-relations?user=${userId}`,
      title: 'BR-partner relations',
    },
    [BrTabItems.BR_SERVICING]: {
      url: `${runtimeConfig.configData.iframeUrls.brCoreIframe}/business-relationship/${brIdFromStore}/br-servicing-roles`,
      title: 'Servicing users',
    },
    [BrTabItems.BR_DELIVERY_INSTRUCTIONS]: {
      url: `${runtimeConfig.configData.iframeUrls.brCoreIframe}/delivery-instruction/${brIdFromStore}`,
      title: 'Delivery instructions',
    },
    [BrTabItems.BLOCKING_INSTRUCTIONS]: {
      url: `${runtimeConfig.configData.iframeUrls.brCoreIframe}/business-relationship/${brIdFromStore}/blocking-instructions`,
      title: 'Blocking Instructions & Restrictions',
    },
    [BrTabItems.PRODUCTS_AND_SERVICES]: {
      url: `${runtimeConfig.configData.iframeUrls.productAndServicesIframe}${pcoUrl}`,
      title: 'Products',
    },
    [BrTabItems.COUNTRY_RISK_WAIVERS]: {
      url: `${runtimeConfig.configData.iframeUrls.productAndServicesIframe}/private-banking/${brIdFromStore}/country-risk-waivers`,
      title: 'Country risk waivers',
    },
    [BrTabItems.PORTFOLIOS]: {
      url: `${runtimeConfig.configData.iframeUrls.productAndServicesIframe}/portfolio-summary/${brIdFromStore}`,
      title: 'Portfolios & cash accounts',
    },
    [BrTabItems.DISTRIBUTION]: {
      url: `${runtimeConfig.configData.iframeUrls.pcdV2Iframe}${pcdV2DistributionUrl}`,
      title: 'Distribution',
    },
    [BrTabItems.DOCUMENT_OVERVIEW]: {
      url: `${runtimeConfig.configData.iframeUrls.pcdV2Iframe}${pcdV2DocOverviewUrl}`,
      title: 'Document overview',
    },
    [BrTabItems.DOC_COLLECTION]: {
      url: `${runtimeConfig.configData.iframeUrls.dossierIframe}/documents/br/${brIdFromStore}`,
      title: 'Doc collection',
    },
    [BrTabItems.INVESTMENT_PROFILE]: {
      url: `${runtimeConfig.configData.iframeUrls.investmentProfileIframe}/investment-profile/br?brId=${brIdFromStore}`,
      title: 'Investment profile',
    },
    [BrTabItems.FINANCIALS]: {
      url: `${runtimeConfig.configData.iframeUrls.wealthOverviewIframe}/financials/br/${brIdFromStore}`,
      title: 'Financials',
    },
    [BrTabItems.BR_PURPOSE]: {
      url: `${runtimeConfig.configData.iframeUrls.brPurposeIframe}/br-purpose/br/${brIdFromStore}`,
      title: 'Purpose',
    },
    [BrTabItems.BR_CONTACT_NOTES]: {
      url: `${runtimeConfig.configData.iframeUrls.contactNoteIframe}/contact-notes/br/${brIdFromStore}`,
      title: 'Contact Notes',
    },
    [BrTabItems.REPORTING]: {
      url: `${runtimeConfig.configData.iframeUrls.pcmReportingIframe}/${brIdFromStore}/zafire`,
      title: 'Reporting',
    },
    [BrTabItems.RISK_CONSIDERATIONS]: {
      url: `${runtimeConfig.configData.iframeUrls.riskConsiderationsIframe}/br-risk-considerations/${brIdFromStore}`,
      title: 'Risk considerations',
    },
    [BrTabItems.CLIENT_REVIEW]: {
      url: `${runtimeConfig.configData.iframeUrls.brApprovalIframe}/client-review/${brIdFromStore}`,
      title: 'Client reviews',
    },
  };

  const { url, title } = tabDetails[selectedTab] || {};

  const baseHeight = HEADER_HEIGHT + TOP_MENU_HEIGHT;
  const tabsHeight = showSecondLevelTabs ? TWO_LEVEL_TABS_HEIGHT : ONE_LEVEL_TABS_HEIGHT;
  const wizardHeight =
    navigationWizardMode.type === 'reduced' || navigationWizardMode.type === 'legacy'
      ? `calc(100vh - ${baseHeight + tabsHeight}px)`
      : `calc(100vh - ${baseHeight}px)`;
  const iframeHeight = `calc(100% - ${baseHeight + tabsHeight}px)`;

  if (navigationWizardMode.type !== 'hidden') {
    return (
      <div
        style={{
          height: wizardHeight,
          overflow: 'hidden',
        }}
      >
        <NavigationWizard
          url={url}
          title={title}
          onNavigate={onNavigate}
          id={`${selectedTab}-iframe`}
          navigationWizardMode={navigationWizardMode}
        />
      </div>
    );
  }

  return <EmbeddedIFrame url={url} title={title} id={`${selectedTab}-iframe`} height={iframeHeight} key={iframeKey} />;
};

export default BrTabsFrames;
